import {
  Avatar,
  Box,
  Divider,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { nothing } from '$func/utils'
import { mapCharsToColor } from '$func/color'
import { toHumanReadablePhone } from '$func/phone'
import { SelectableAvatar } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'

const StyledDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const StyledListItem = styled(ListItem)(() => ({
  height: 56,
  minHeight: 56,
}))

interface StyledListItemButtonProps {
  hasActionComponent: boolean
  disableRipple: boolean
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'hasActionComponent',
})<StyledListItemButtonProps>`
  height: 100%;
  box-sizing: border-box;
  padding: 0 15px 0 ${props => (props.hasActionComponent ? 0 : 10)}px;
  overflow: hidden;
`

interface Props {
  firstname: string
  lastname: string
  phone: string
  secondaryActionComponent?: JSX.Element
  hasDivider?: boolean
  isTransparent?: boolean
  onClick?: () => unknown
  isPassive: boolean
  selected: boolean
}

export function InviteeListItem({
  firstname,
  lastname,
  phone,
  secondaryActionComponent,
  hasDivider = true,
  isTransparent = false,
  onClick,
  isPassive,
  selected,
}: Props) {
  const avatarText = `${firstname[0] || ''}‌${lastname?.[0] ||
    ''}`.toUpperCase()

  const displayName = [firstname, lastname].filter(Boolean).join(' ')

  return (
    <>
      <StyledListItem
        disablePadding
        sx={{
          bgcolor: isTransparent ? 'transparent' : 'white',
          opacity: isPassive ? 0.5 : 1,
        }}
      >
        <StyledListItemButton
          hasActionComponent={!!secondaryActionComponent}
          disableRipple={isPassive}
          onClick={isPassive ? nothing : onClick}
        >
          <ListItemAvatar>
            <SelectableAvatar
              avatarBackgroundText={avatarText}
              backgroundColor={mapCharsToColor(avatarText)}
              onClick={onClick}
              sx={{
                cursor: onClick ? 'pointer' : 'default',
              }}
              checked={selected}
              size={AvatarSize.MD}
            />
          </ListItemAvatar>

          <Stack
            justifyContent="center"
            sx={{ width: '100%', overflow: 'hidden' }}
          >
            <Box
              sx={{ display: 'flex', overflow: 'hidden', marginBottom: '1px' }}
            >
              <Typography
                variant="subtitle3"
                noWrap
                color={isTransparent ? 'white' : undefined}
              >
                {displayName}
              </Typography>
            </Box>

            <Typography
              variant="caption1"
              noWrap
              sx={{ color: !isTransparent ? '#9D9FAA' : 'white' }}
            >
              {toHumanReadablePhone(phone)}
            </Typography>
          </Stack>
        </StyledListItemButton>

        {secondaryActionComponent}
      </StyledListItem>

      {hasDivider && <StyledDivider variant="inset" />}
    </>
  )
}
